import type { TFunction } from 'i18next';
import type { Dispatch } from 'react';
import type { NavigateFunction } from 'react-router';
import type { UserLocalStorage } from '../../types/localStorage';

import TagManager from 'react-gtm-module';
import { toast } from 'react-hot-toast';

import { getMaskedOrganizationName } from '../helpers';
import { postLogin } from './auth-requests';
import { parseResponseErrors, ResponseError } from './response-errors';

const LOGIN_ERROR_MESSAGES: Record<string, string> = {
  invalid_credentials: 'Global.ErrorInvalidLoginOrPassword',
  user_disabled: 'Login.UserDisabled',
  user_not_activated: 'Login.UserNotActivated',
};

const getLoginErrorMessage = (error: ResponseError, t: TFunction): string => {
  if (error.status === 429) {
    return error.retryAfter
      ? t('Login.TooManyAttemptsWithTime', {
          minutes: Math.ceil(error.retryAfter / 60),
        })
      : t('Login.TooManyAttempts');
  }

  const messageTranslationKey = LOGIN_ERROR_MESSAGES[error.message];
  return messageTranslationKey ? t(messageTranslationKey) : error.message;
};

/**
 * Fetch login callback. Logs user and sends data to GTM.
 * If login is successful, it navigates to the specified URL, otherwise it shows an error message.
 */
export async function fetchLogin(
  values: { email: string; password: string },
  setUser: Dispatch<UserLocalStorage>,
  t: TFunction,
  navigate?: NavigateFunction,
  navigateUrl: string = '',
): Promise<[string | null, Record<string, string | string[]> | null]> {
  try {
    const { body, status, ok, retryAfter } = await postLogin(
      values.email,
      values.password,
    );
    if (!ok) {
      throw parseResponseErrors(body, status, retryAfter);
    }
    setUser(body);

    TagManager.dataLayer({
      dataLayer: {
        event: 'login',
        user_id: body.data?.id,
        organization_id: body.data?.organization,
        organization_name: getMaskedOrganizationName(
          body.data?.organization_name,
        ),
        plan_id: body.data?.limits_plan?.id,
        plan_name: body.data?.limits_plan?.name,
        space_id: body.data?.default_space?.id,
        user_roles: body.data?.roles,
      },
    });

    if (navigate) navigate(navigateUrl || '/');
    return [body.token, null];
  } catch (error) {
    let errors;

    if (!(error instanceof ResponseError)) {
      toast.error(t('Form.CommunicationErrorMessage'));
      errors = { global: t('Form.CommunicationErrorMessage') };
    } else {
      const message = getLoginErrorMessage(error, t);
      toast.error(message);
      errors =
        Object.keys(LOGIN_ERROR_MESSAGES).includes(error.message) ||
        error.status === 429
          ? { global: message }
          : { ...error.errors };
    }
    return [null, errors];
  }
}
